import React from "react";
import { ArrowUUpLeft, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import useUndoImportBatch from "@/hooks/useUndoImportBatch";

function fmtCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Sticky yellow banner offering a 5-minute undo of the most recent CSV
 * wizard import. All state + effects live in `useUndoImportBatch` — this
 * component is a thin presenter so the render tree stays under 40 lines.
 */
export default function UndoImportBanner() {
  const { user } = useAuth();
  const { visible, batch, remaining, busy, undo, dismiss } = useUndoImportBatch(user);
  if (!visible) return null;

  const uploaderNote =
    batch.uploader_id === user?.id ? "you" : (batch.uploader_name || "another admin");

  return (
    <div
      className="bg-yellow-300 border-b-2 border-black px-6 py-2 flex items-center gap-3 text-xs font-mono"
      data-testid="undo-import-banner"
    >
      <ArrowUUpLeft size={14} weight="bold" />
      <div className="flex-1 min-w-0">
        <b>{batch.row_count}</b> lead{batch.row_count === 1 ? "" : "s"} imported by {uploaderNote}
        {batch.duplicate_count > 0 && <> · {batch.duplicate_count} duplicate</>}
        {batch.capped_count > 0 && <> · {batch.capped_count} capped</>}
        {" · "}Undo window <b data-testid="undo-countdown">{fmtCountdown(remaining)}</b>
      </div>
      <button
        onClick={undo}
        disabled={busy}
        data-testid="undo-import-button"
        className="border-2 border-black bg-black text-white px-3 py-1 uppercase tracking-widest font-bold hover:bg-red-700 disabled:opacity-60"
      >
        {busy ? "Undoing…" : "Undo import"}
      </button>
      <button
        onClick={dismiss}
        data-testid="undo-import-dismiss"
        className="border border-black p-1 hover:bg-black hover:text-white"
        aria-label="Dismiss"
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}
